import { useState } from 'react';
import { Platform } from 'react-native';
import type { useRouter } from 'expo-router';

import { ROUTES } from '../../../navigation/routes';
import { billingAPI, getBillingCheckoutConflict } from '../../../services/api';
import { trackAuthEvent } from '../../../services/analytics';
import { showDialog } from '../../../utils/dialogs';
import type { useChangePlan } from './useChangePlan';
import { backendDetailText } from '../planBilling';
import type { Translate, UserProfile } from '../profileTypes';

interface UseSubscriptionActionsOptions {
  profile: UserProfile | null;
  isCardBackedTrial: boolean;
  loadProfile: () => Promise<void>;
  redirectDeadSubscriptionToCheckout: ReturnType<typeof useChangePlan>['redirectDeadSubscriptionToCheckout'];
  router: ReturnType<typeof useRouter>;
  t: Translate;
}

/** Billing portal, plan selection and resume-subscription actions. */
export function useSubscriptionActions({
  profile,
  isCardBackedTrial,
  loadProfile,
  redirectDeadSubscriptionToCheckout,
  router,
  t,
}: UseSubscriptionActionsOptions) {
  const [resumeLoading, setResumeLoading] = useState(false);

  const handleManageSubscription = async () => {
    trackAuthEvent('subscription_management_clicked', {
      plan_tier: profile?.subscription?.plan_tier,
      subscription_status: profile?.subscription?.status,
      platform: Platform.OS,
    });
    // Open window immediately to avoid popup blocker (must be in user interaction context)
    const portalWindow: Window | null = window.open('about:blank', '_blank');

    try {
      const response = await billingAPI.portal();
      trackAuthEvent('billing_portal_opened', { platform: Platform.OS });
      const url = response.url;
      if (portalWindow) {
        portalWindow.location.href = url;
      } else {
        // Fallback if popup was blocked
        (window as any).location.href = url;
      }
    } catch (error: any) {
      // Close the blank window if there was an error
      if (portalWindow) {
        portalWindow.close();
      }
      const errorStatus = error?.response?.status;
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('billing_portal_failed', { error_type: errorType });
      const message = error.response?.data?.detail || t('profile.billingPortalFailed');
      showDialog(t('common.error'), message);
    }
  };

  const handleChoosePlan = () => {
    trackAuthEvent('plan_selection_opened', { source: 'profile' });
    router.push(ROUTES.GATED_PLAN_SELECTION as any);
  };

  const handleResumeSubscription = async () => {
    if (resumeLoading) return;
    setResumeLoading(true);
    try {
      const resumingCardTrial = isCardBackedTrial;
      trackAuthEvent('subscription_resume_submitted');
      await billingAPI.resumeSubscription();
      await loadProfile();
      trackAuthEvent('subscription_resume_completed');
      showDialog(
        t('common.success'),
        resumingCardTrial
          ? t('profile.resumeTrialSuccess')
          : t('profile.resumeSuccess'),
      );
    } catch (error: any) {
      const conflict = getBillingCheckoutConflict(error);
      if (conflict?.action === 'checkout') {
        await redirectDeadSubscriptionToCheckout('subscription_resume_redirected_to_checkout');
        return;
      }
      const errorStatus = error?.response?.status;
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('subscription_resume_failed', {
        error_type: errorType,
      });
      const detail = backendDetailText(error);
      const statusMatch = detail.match(/status '([^']+)'/i);
      let message = t('profile.resumeFailed');
      if (errorStatus === 400 && /No subscription to resume/i.test(detail)) {
        message = t('profile.noActiveSubscription');
      } else if (errorStatus === 400 && /Cannot resume subscription with status/i.test(detail)) {
        message = t('profile.cannotResumeSubscriptionStatus', {
          status: statusMatch?.[1] || profile?.subscription?.status || t('profile.planUnknown'),
        });
      } else if (errorStatus === 400 && /not scheduled for cancellation/i.test(detail)) {
        message = t('profile.subscriptionNotScheduledForCancellation');
      } else if (errorStatus === 502) {
        message = t('profile.billingActionTemporarilyUnavailable');
      } else if (detail && !errorStatus) {
        message = detail;
      }
      showDialog(t('common.error'), message);
    } finally {
      setResumeLoading(false);
    }
  };

  return {
    handleManageSubscription,
    handleChoosePlan,
    handleResumeSubscription,
    resumeLoading,
  };
}
